<template>
  <div class="dashboard-page">
    <PageHeader
      title="Dashboard"
      subtitle="Your subscriptions, usage, and quick actions at a glance."
    />

    <v-row>
      <!-- Web Scraping API subscription -->
      <v-col cols="12" md="6">
        <SectionCard title="Web Scraping API" class="dashboard-page__card">
          <template v-if="!!subscription && !!subscription.plan">
            <dl class="dashboard-page__meta">
              <div class="dashboard-page__meta-row">
                <dt>Current plan</dt>
                <dd>
                  <router-link to="/subscriptions">{{
                    subscription.plan.name
                  }}</router-link>
                </dd>
              </div>
              <div class="dashboard-page__meta-row">
                <dt>Start date</dt>
                <dd>{{ getUTCDateString(subscription.start_time) }}</dd>
              </div>
              <div class="dashboard-page__meta-row">
                <dt>Renewal date</dt>
                <dd>{{ getUTCDateString(subscription.end_time) }}</dd>
              </div>
              <div class="dashboard-page__meta-row">
                <dt>Auto-restart</dt>
                <dd>
                  <router-link to="/webscrapingapi">{{
                    api_renew_status ? "Enabled" : "Disabled"
                  }}</router-link>
                </dd>
              </div>
              <div class="dashboard-page__meta-row">
                <dt>Concurrency limit</dt>
                <dd>{{ subscription.plan.price === 0 ? "1" : "Unlimited" }}</dd>
              </div>
            </dl>

            <div class="dashboard-page__credentials">
              <CredentialField
                label="API token"
                :value="api_token || ''"
                secret
              />
            </div>

            <StatCard
              class="dashboard-page__stat"
              label="API credits used"
              :value="subscription.total_credits - subscription.remained_credits"
              :max="subscription.total_credits"
              :caption="creditsCaption"
            />

            <p
              v-if="subscription.remained_credits === 0"
              class="dashboard-page__hint"
            >
              You've run out of API credits. Please
              <router-link to="/subscriptions">upgrade your plan</router-link>
              or
              <a href="mailto:support@scrapingant.com">contact us</a>
              for a custom plan.
            </p>
          </template>
          <template v-else>
            <p class="dashboard-page__empty">
              No active subscription. Please
              <a @click="reload">reload</a>
              the page or try again later.
            </p>
          </template>
          <template #actions>
            <v-btn text color="primary" to="/webscrapingapi"
              >Open API panel</v-btn
            >
          </template>
        </SectionCard>
      </v-col>

      <!-- Residential proxies subscription -->
      <v-col cols="12" md="6">
        <SectionCard title="Residential proxies" class="dashboard-page__card">
          <template
            v-if="
              !!residential_subscription && !!residential_subscription.plan_name
            "
          >
            <dl class="dashboard-page__meta">
              <div class="dashboard-page__meta-row">
                <dt>Current plan</dt>
                <dd>
                  <router-link to="/subscriptions?tab=residential">{{
                    residential_subscription.plan_name
                  }}</router-link>
                </dd>
              </div>
              <div class="dashboard-page__meta-row">
                <dt>Start date</dt>
                <dd>{{ parseDate(residential_subscription.start_date) }}</dd>
              </div>
              <div class="dashboard-page__meta-row">
                <dt>
                  {{
                    residential_subscription.recurring
                      ? "Renewal date"
                      : "End date"
                  }}
                </dt>
                <dd>{{ parseDate(residential_subscription.end_date) }}</dd>
              </div>
            </dl>

            <div class="dashboard-page__credentials">
              <CredentialField
                label="Username"
                :value="residential_credentials.proxy_username || ''"
              />
              <CredentialField
                label="Password"
                :value="residential_credentials.proxy_secret || ''"
                secret
              />
            </div>

            <StatCard
              class="dashboard-page__stat"
              label="Traffic used"
              :value="residential_subscription.traffic_used_gb"
              :max="residential_subscription.traffic_limit_gb"
              unit="GB"
              :caption="residentialCaption"
            />

            <p
              v-if="
                residential_subscription.traffic_used_gb >=
                residential_subscription.traffic_limit_gb
              "
              class="dashboard-page__hint"
            >
              You've run out of traffic. Please
              <router-link to="/subscriptions?tab=residential"
                >upgrade your plan</router-link
              >
              or
              <a href="mailto:support@scrapingant.com">contact us</a>.
            </p>
          </template>
          <template v-else>
            <p class="dashboard-page__empty">
              No active subscription.
              <router-link :to="{ path: '/subscriptions?tab=residential' }"
                >Subscribe</router-link
              >
              to get residential proxy access.
            </p>
          </template>
          <template #actions>
            <v-btn text color="primary" to="/residential">Open panel</v-btn>
          </template>
        </SectionCard>
      </v-col>

    </v-row>

    <!-- Quick links -->
    <v-row class="mt-2">
      <v-col cols="12" md="6">
        <SectionCard class="hover-lift dashboard-page__link-card">
          <div class="dashboard-page__link-head">
            <v-icon color="primary" size="22">mdi-robot-outline</v-icon>
            <h3>Web Scraping API UI helper</h3>
          </div>
          <p>
            Explore the full Web Scraping API potential with the visual request
            generator.
          </p>
          <template #actions>
            <v-btn text color="primary" to="/webscrapingapi"
              >Make a request</v-btn
            >
          </template>
        </SectionCard>
      </v-col>
      <v-col cols="12" md="6">
        <SectionCard class="hover-lift dashboard-page__link-card">
          <div class="dashboard-page__link-head">
            <v-icon color="primary" size="22"
              >mdi-account-network-outline</v-icon
            >
            <h3>Fastest residential proxies</h3>
          </div>
          <p>
            Unlimited-concurrency residential proxies with no use-case
            restrictions.
          </p>
          <template #actions>
            <v-btn text color="primary" to="/residential">Setup proxy</v-btn>
          </template>
        </SectionCard>
      </v-col>
    </v-row>

    <!-- Usage charts -->
    <v-row class="mt-2">
      <v-col cols="12">
        <SectionCard
          title="Web Scraping API usage"
          subtitle="Credits consumed since the current subscription started."
        >
          <div class="dashboard-page__chart-wrap">
            <usage-chart
              :start-date="getUTCDateString(subscription.start_time)"
              :chart-data="chartdata"
              :height="260"
            />
          </div>
        </SectionCard>
      </v-col>
      <v-col cols="12">
        <SectionCard
          title="Residential proxies usage"
          subtitle="Traffic consumed on the residential proxy plan."
        >
          <div class="dashboard-page__chart-wrap">
            <usage-chart
              :start-date="parseDate(residential_subscription.start_date)"
              :chart-data="residential_chartdata"
              :height="260"
            />
          </div>
        </SectionCard>
      </v-col>
    </v-row>

    <div class="loading-overlay" v-if="loading">
      <v-progress-circular
        :size="50"
        :width="2"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
  </div>
</template>

<script>
import UsageChart from "../views/UsageChart";
import getDateUTCString from "@/utils/date_utils";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import CredentialField from "@/components/shared/CredentialField";

const PRIMARY = "#E2782F";
const PRIMARY_ALPHA = "#E2782F99";
const TRANSPARENT = "#FFFFFF00";

const buildPrimaryDataset = (label, data) => ({
  label,
  lineTension: 0.2,
  borderWidth: 2,
  pointRadius: 2,
  backgroundColor: TRANSPARENT,
  borderColor: PRIMARY,
  pointBorderColor: PRIMARY,
  pointBackgroundColor: PRIMARY,
  data,
});

const buildSecondaryDataset = (label, data, i) => ({
  label,
  lineTension: 0.2,
  borderWidth: 2,
  pointRadius: 2,
  backgroundColor: PRIMARY_ALPHA,
  borderColor: PRIMARY_ALPHA,
  pointBorderColor: PRIMARY_ALPHA,
  pointBackgroundColor: PRIMARY_ALPHA,
  borderDash: [(i + 1) * 2, i + 1],
  data,
});

const emptyChart = (label) => ({
  labels: [],
  datasets: [buildPrimaryDataset(label, [])],
});

export default {
  name: "Dashboard",
  components: {
    UsageChart,
    PageHeader,
    SectionCard,
    StatCard,
    CredentialField,
  },
  data() {
    return {
      api_token: "",
      subscription: {},
      api_renew_status: false,
      residential_subscription: {},
      residential_credentials: {},
      chartdata: emptyChart("API credits usage"),
      residential_chartdata: emptyChart("GBs used"),
      loading: false,
      result_loading: false,
      usage_grapph_loading: false,
      residential_usage_grapph_loading: false,
    };
  },
  computed: {
    usageValue() {
      return this.subscription
        ? (1 -
            parseFloat(this.subscription.remained_credits) /
              parseFloat(this.subscription.total_credits)) *
            100
        : 0;
    },
    residentialUsageValue() {
      return this.residential_subscription
        ? (this.residential_subscription.traffic_used_gb /
            this.residential_subscription.traffic_limit_gb) *
            100
        : 0;
    },
    creditsCaption() {
      if (!this.subscription || !this.subscription.total_credits) return "";
      const used =
        this.subscription.total_credits - this.subscription.remained_credits;
      return `${used.toLocaleString()} of ${this.subscription.total_credits.toLocaleString()} used`;
    },
    residentialCaption() {
      if (!this.residential_subscription || !this.residential_subscription.traffic_limit_gb)
        return "";
      return `${this.residential_subscription.traffic_used_gb.toLocaleString()} of ${this.residential_subscription.traffic_limit_gb.toLocaleString()} GB used`;
    },
  },
  methods: {
    getToken() {
      return this.$store.dispatch("dashboard/getApiToken").then(
        (res) => {
          this.api_token = res.data.token;
        },
        (err) => {
          console.log(err);
        }
      );
    },

    applyUsageGraph(res, targetKey, label) {
      const next = Object.assign({}, this[targetKey]);
      next.labels = res.data.labels;
      next.datasets = [buildPrimaryDataset(label, res.data.data)];
      if (res.data.detailed_view) {
        res.data.detailed_view.forEach((userData, i) => {
          next.datasets.push(buildSecondaryDataset(userData.email, userData.data, i));
        });
      }
      this[targetKey] = next;
    },

    getUsageGraph() {
      this.usage_grapph_loading = true;
      return this.$store.dispatch("dashboard/getUsageGraph").then(
        (res) => {
          this.usage_grapph_loading = false;
          this.applyUsageGraph(res, "chartdata", "API credits usage");
        },
        () => {
          this.usage_grapph_loading = false;
        }
      );
    },

    getResidentialUsageGraph() {
      this.residential_usage_grapph_loading = true;
      return this.$store
        .dispatch("dashboard/getResidentialUsageGraph")
        .then(
          (res) => {
            this.residential_usage_grapph_loading = false;
            this.applyUsageGraph(res, "residential_chartdata", "GBs used");
          },
          () => {
            this.residential_usage_grapph_loading = false;
          }
        );
    },

    getCurrentSubscription() {
      return this.$store
        .dispatch("dashboard/getCurrentSubscription")
        .then((res) => {
          this.subscription = res.data.subscriptions[0];
        }, console.error);
    },

    getCurrentResidentialSubscription() {
      return this.$store
        .dispatch("dashboard/getCurrentResidentialSubscription")
        .then((res) => {
          this.residential_subscription = res;
        }, console.error);
    },

    getResidentialCredentials() {
      return this.$store
        .dispatch("dashboard/getResidentialCredentials")
        .then((res) => {
          this.residential_credentials = res;
        }, console.error);
    },

    getAPIRenewStatus() {
      return this.$store
        .dispatch("dashboard/getAPIRenewStatus")
        .then((res) => {
          this.api_renew_status = res.auto_prolong_web_scraping_api;
        }, console.error);
    },

    parseDate(date) {
      return getDateUTCString(new Date(date));
    },

    getUTCDateString(timestamp) {
      return getDateUTCString(new Date(timestamp * 1000));
    },

    reload() {
      this.$router.go();
    },
  },
  mounted() {
    this.loading = true;
    Promise.all([
      this.getToken(),
      this.getCurrentSubscription(),
      this.getCurrentResidentialSubscription(),
      this.getResidentialCredentials(),
      this.getAPIRenewStatus(),
    ]).then(
      () => (this.loading = false),
      () => (this.loading = false)
    );
    this.getUsageGraph();
    this.getResidentialUsageGraph();
  },
};
</script>

<style scoped lang="scss">
.dashboard-page {
  position: relative;

  &__card {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__meta {
    margin: 0 0 16px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__meta-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    line-height: 1.5;

    dt {
      color: rgba(15, 23, 42, 0.58);
      font-weight: 500;
      margin: 0;
    }

    dd {
      color: #0f172a;
      font-weight: 500;
      margin: 0;
      text-align: right;

      a {
        color: #e2782f;
        font-weight: 600;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  &__credentials {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 18px;
  }

  &__stat {
    margin-top: auto;
  }

  &__hint {
    font-size: 12px;
    color: rgba(15, 23, 42, 0.62);
    line-height: 1.5;
    margin: 14px 0 0;

    a {
      color: #e2782f;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__empty {
    font-size: 14px;
    color: rgba(15, 23, 42, 0.62);
    margin: 0;

    a {
      color: #e2782f;
      cursor: pointer;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__link-card {
    height: 100%;

    h3 {
      font-size: 15px;
      font-weight: 600;
      color: #0f172a;
      margin: 0;
    }

    p {
      font-size: 13px;
      color: rgba(15, 23, 42, 0.62);
      line-height: 1.55;
      margin: 0;
    }
  }

  &__link-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  &__chart-wrap {
    position: relative;
    height: 260px;
    width: 100%;
    overflow: hidden;

    ::v-deep > div {
      height: 100% !important;
      width: 100% !important;
    }

    ::v-deep canvas {
      max-height: 100%;
    }
  }
}

@media (max-width: 600px) {
  .dashboard-page__chart-wrap {
    height: 220px;
  }
}
</style>
